// AdminCourseBuilder.js — Fase 15.5: Constructor visual de cursos mentor
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ADMIN_COLORS, AdminButton, AdminSmallButton, AdminModalOverlay, AdminModalContent,
  AdminModalTitle, AdminModalActions, AdminFormGroup, AdminLabel, AdminInput,
  AdminTextarea, AdminLoading, AdminEmptyState, AdminInfoText, AdminBadge
} from './AdminShared';
import { mentorCursoService } from '../../services/api';
import MediaSelector from './MediaSelector';

// ============================================================
// STYLED COMPONENTS
// ============================================================

const BuilderContainer = styled.div`
  background: ${ADMIN_COLORS.bg}; border-radius: 12px; padding: 1.25rem;
  min-height: 300px;
`;

const CourseHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 1.25rem; padding-bottom: 0.75rem; border-bottom: 2px solid ${ADMIN_COLORS.border};
`;

const ModuleCard = styled.div`
  background: ${ADMIN_COLORS.white}; border-radius: 10px; margin-bottom: 0.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06); border-left: 4px solid ${ADMIN_COLORS.primary};
  overflow: hidden;
`;

const ModuleHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.75rem 1rem; cursor: pointer; user-select: none;
  &:hover { background: ${ADMIN_COLORS.bgHover}; }
`;

const ModuleTitle = styled.div`
  font-weight: 600; font-size: 0.95rem; color: ${ADMIN_COLORS.primary};
  display: flex; align-items: center; gap: 0.5rem;
`;

const LessonCard = styled.div`
  background: ${ADMIN_COLORS.bgHover}; margin: 0 0.75rem 0.5rem 1.5rem;
  border-radius: 8px; border-left: 3px solid ${ADMIN_COLORS.accent};
`;

const LessonHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.6rem 0.75rem; cursor: pointer;
  &:hover { background: #e8ecf0; border-radius: 8px; }
`;

const LessonTitle = styled.div`
  font-weight: 500; font-size: 0.88rem; color: ${ADMIN_COLORS.text};
  display: flex; align-items: center; gap: 0.4rem;
`;

const ItemRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.4rem 0.75rem; margin: 0 0.5rem 0.25rem 1rem;
  background: ${ADMIN_COLORS.white}; border-radius: 6px;
  border: 1px solid ${ADMIN_COLORS.borderLight}; font-size: 0.83rem;
`;

const ItemInfo = styled.div`
  display: flex; align-items: center; gap: 0.4rem; color: ${ADMIN_COLORS.text};
  flex: 1; min-width: 0;
  span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const DragHandle = styled.span`
  cursor: grab; color: ${ADMIN_COLORS.textMuted}; font-size: 1.1rem;
  padding: 0 4px; &:active { cursor: grabbing; }
`;

const AddButton = styled.button`
  background: transparent; border: 1.5px dashed ${ADMIN_COLORS.border};
  border-radius: 8px; padding: 0.5rem; width: 100%; text-align: center;
  cursor: pointer; color: ${ADMIN_COLORS.textLight}; font-size: 0.83rem;
  transition: all 0.2s;
  &:hover { border-color: ${ADMIN_COLORS.accent}; color: ${ADMIN_COLORS.accent}; background: #f0fdfa; }
`;

const CountBadge = styled.span`
  background: ${ADMIN_COLORS.bg}; color: ${ADMIN_COLORS.textLight};
  padding: 1px 8px; border-radius: 10px; font-size: 0.7rem; font-weight: 600;
`;

const ActionGroup = styled.div`
  display: flex; gap: 0.25rem; align-items: center;
`;

const MiniBtn = styled.button`
  background: none; border: none; cursor: pointer; padding: 2px 5px;
  color: ${p => p.$danger ? ADMIN_COLORS.danger : ADMIN_COLORS.textMuted};
  font-size: 0.8rem; border-radius: 4px; transition: all 0.15s;
  &:hover { background: ${p => p.$danger ? '#fef2f2' : ADMIN_COLORS.bgHover};
    color: ${p => p.$danger ? ADMIN_COLORS.danger : ADMIN_COLORS.accent}; }
`;

const DurationSummary = styled.div`
  font-size: 0.78rem; color: ${ADMIN_COLORS.textLight}; margin-top: 2px;
`;

const TYPE_ICONS = { video: '🎬', pdf: '📑', imagen: '🖼️', audio: '🎵', presentacion: '📊', otro: '📄' };
const formatDuration = (s) => s ? Math.floor(s/60) + ':' + String(s%60).padStart(2,'0') : '';

// ============================================================
// SORTABLE MODULE
// ============================================================
const SortableModule = ({ module, children, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: `mod-${module.id}` });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return <div ref={setNodeRef} style={style} {...attributes}>{children(listeners)}</div>;
};

// ============================================================
// SORTABLE LESSON
// ============================================================
const SortableLesson = ({ lesson, children, ...props }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: `lec-${lesson.id}` });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return <div ref={setNodeRef} style={style} {...attributes}>{children(listeners)}</div>;
};

// ============================================================
// SORTABLE ITEM
// ============================================================
const SortableItem = ({ item, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: `item-${item.id}` });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return <div ref={setNodeRef} style={style} {...attributes}>{children(listeners)}</div>;
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const AdminCourseBuilder = ({ documentoId, documentoTitulo, proyectoId }) => {
  const [curso, setCurso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedLessons, setExpandedLessons] = useState({});
  const [showMediaSelector, setShowMediaSelector] = useState(null); // leccionId
  const [showNameModal, setShowNameModal] = useState(null); // {type, parentId, editing?}
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const loadEstructura = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mentorCursoService.getEstructura(documentoId);
      const cursos = res.data.cursos || [];
      setCurso(cursos[0] || null);
      // Auto-expand all modules
      if (cursos[0]) {
        const exp = {};
        (cursos[0].modulos || []).forEach(m => { exp[m.id] = true; });
        setExpandedModules(exp);
      }
    } catch (e) { console.error('Error cargando estructura:', e); }
    setLoading(false);
  }, [documentoId]);

  useEffect(() => { loadEstructura(); }, [loadEstructura]);

  // Create course if none exists
  const handleCreateCurso = async () => {
    setSaving(true);
    try {
      await mentorCursoService.createCurso({
        documento_id: documentoId,
        proyecto_id: proyectoId,
        titulo: documentoTitulo || 'Curso sin titulo',
      });
      await loadEstructura();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
  };

  // Add module
  const handleAddModulo = () => {
    setShowNameModal({ type: 'modulo', parentId: curso.id });
  };

  // Add lesson
  const handleAddLeccion = (moduloId) => {
    setShowNameModal({ type: 'leccion', parentId: moduloId });
  };

  // Name modal submit
  const handleNameSubmit = async (titulo) => {
    const { type, parentId, editId } = showNameModal;
    setSaving(true);
    try {
      if (editId) {
        if (type === 'modulo') await mentorCursoService.updateModulo(editId, { titulo });
        else if (type === 'leccion') await mentorCursoService.updateLeccion(editId, { titulo });
      } else {
        if (type === 'modulo') await mentorCursoService.createModulo({ curso_id: parentId, titulo });
        else if (type === 'leccion') await mentorCursoService.createLeccion({ modulo_id: parentId, titulo });
      }
      await loadEstructura();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setSaving(false);
    setShowNameModal(null);
  };

  // Delete module/lesson
  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Eliminar ${type === 'modulo' ? 'modulo' : 'leccion'} "${name}"?`)) return;
    try {
      if (type === 'modulo') await mentorCursoService.deleteModulo(id);
      else await mentorCursoService.deleteLeccion(id);
      await loadEstructura();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  // Add media item to lesson
  const handleAddMedia = async (media) => {
    const leccionId = showMediaSelector;
    try {
      const items = Array.isArray(media) ? media : [media];
      for (const m of items) {
        await mentorCursoService.addItem({ leccion_id: leccionId, media_id: m.id });
      }
      await loadEstructura();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setShowMediaSelector(null);
  };

  // Remove item
  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Remover este contenido de la leccion?')) return;
    try {
      await mentorCursoService.removeItem(itemId);
      await loadEstructura();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  // Drag end handler for modules
  const handleDragEndModules = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const modulos = curso.modulos;
    const oldIndex = modulos.findIndex(m => `mod-${m.id}` === active.id);
    const newIndex = modulos.findIndex(m => `mod-${m.id}` === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(modulos, oldIndex, newIndex);
    setCurso({ ...curso, modulos: reordered });

    try {
      await mentorCursoService.reorder('modulos',
        reordered.map((m, i) => ({ id: m.id, orden: i + 1 }))
      );
    } catch (e) { console.error('Error reordenando:', e); loadEstructura(); }
  };

  // Drag end for lessons within a module
  const handleDragEndLessons = async (event, moduloId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const modulo = curso.modulos.find(m => m.id === moduloId);
    if (!modulo) return;

    const lecciones = modulo.lecciones;
    const oldIndex = lecciones.findIndex(l => `lec-${l.id}` === active.id);
    const newIndex = lecciones.findIndex(l => `lec-${l.id}` === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(lecciones, oldIndex, newIndex);
    const updatedModulos = curso.modulos.map(m =>
      m.id === moduloId ? { ...m, lecciones: reordered } : m
    );
    setCurso({ ...curso, modulos: updatedModulos });

    try {
      await mentorCursoService.reorder('lecciones',
        reordered.map((l, i) => ({ id: l.id, orden: i + 1 }))
      );
    } catch (e) { console.error('Error reordenando:', e); loadEstructura(); }
  };

  // Drag end for items within a lesson
  const handleDragEndItems = async (event, leccionId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    let leccion = null;
    for (const m of curso.modulos) {
      leccion = m.lecciones.find(l => l.id === leccionId);
      if (leccion) break;
    }
    if (!leccion) return;

    const items = leccion.items;
    const oldIndex = items.findIndex(i => `item-${i.id}` === active.id);
    const newIndex = items.findIndex(i => `item-${i.id}` === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(items, oldIndex, newIndex);

    // Update local state
    const updatedModulos = curso.modulos.map(m => ({
      ...m, lecciones: m.lecciones.map(l =>
        l.id === leccionId ? { ...l, items: reordered } : l
      )
    }));
    setCurso({ ...curso, modulos: updatedModulos });

    try {
      await mentorCursoService.reorder('items',
        reordered.map((it, i) => ({ id: it.id, orden: i + 1 }))
      );
    } catch (e) { console.error('Error reordenando:', e); loadEstructura(); }
  };

  const toggleModule = (id) => setExpandedModules(p => ({ ...p, [id]: !p[id] }));
  const toggleLesson = (id) => setExpandedLessons(p => ({ ...p, [id]: !p[id] }));

  // Compute totals
  const getTotals = () => {
    if (!curso) return { modulos: 0, lecciones: 0, items: 0, duracion: 0 };
    let lecciones = 0, items = 0, duracion = 0;
    (curso.modulos || []).forEach(m => {
      lecciones += (m.lecciones || []).length;
      (m.lecciones || []).forEach(l => {
        items += (l.items || []).length;
        (l.items || []).forEach(it => { duracion += parseInt(it.duracion_segundos) || 0; });
      });
    });
    return { modulos: (curso.modulos || []).length, lecciones, items, duracion };
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (loading) return <AdminLoading>Cargando estructura del curso...</AdminLoading>;

  if (!curso) {
    return (
      <BuilderContainer>
        <AdminEmptyState>
          <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>📚</div>
          <p>Este documento no tiene un curso mentor configurado.</p>
          <AdminButton onClick={handleCreateCurso} disabled={saving} style={{ marginTop: '0.75rem' }}>
            {saving ? 'Creando...' : 'Crear Curso Mentor'}
          </AdminButton>
        </AdminEmptyState>
      </BuilderContainer>
    );
  }

  const totals = getTotals();
  const modulos = curso.modulos || [];

  return (
    <BuilderContainer>
      <CourseHeader>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', color: ADMIN_COLORS.primary }}>
            📚 {curso.titulo}
          </div>
          <DurationSummary>
            {totals.modulos} modulos &middot; {totals.lecciones} lecciones &middot; {totals.items} contenidos
            {totals.duracion > 0 && <> &middot; {Math.floor(totals.duracion / 60)} min total</>}
          </DurationSummary>
        </div>
        <AdminButton onClick={handleAddModulo}>+ Modulo</AdminButton>
      </CourseHeader>

      {modulos.length === 0 ? (
        <AdminEmptyState>
          No hay modulos. Agrega el primero con el boton "+ Modulo".
        </AdminEmptyState>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndModules}>
          <SortableContext items={modulos.map(m => `mod-${m.id}`)} strategy={verticalListSortingStrategy}>
            {modulos.map((modulo, mi) => (
              <SortableModule key={modulo.id} module={modulo}>
                {(dragListeners) => (
                  <ModuleCard>
                    <ModuleHeader onClick={() => toggleModule(modulo.id)}>
                      <ModuleTitle>
                        <DragHandle {...dragListeners} onClick={e => e.stopPropagation()}>⠿</DragHandle>
                        <span>{expandedModules[modulo.id] ? '▼' : '▶'}</span>
                        <span>Modulo {mi + 1}: {modulo.titulo}</span>
                        <CountBadge>{(modulo.lecciones || []).length} lecciones</CountBadge>
                      </ModuleTitle>
                      <ActionGroup>
                        <MiniBtn onClick={e => { e.stopPropagation(); setShowNameModal({ type: 'modulo', parentId: curso.id, editId: modulo.id, defaultValue: modulo.titulo }); }}>✏️</MiniBtn>
                        <MiniBtn $danger onClick={e => { e.stopPropagation(); handleDelete('modulo', modulo.id, modulo.titulo); }}>🗑</MiniBtn>
                      </ActionGroup>
                    </ModuleHeader>

                    {expandedModules[modulo.id] && (
                      <div style={{ padding: '0.25rem 0 0.75rem' }}>
                        <DndContext sensors={sensors} collisionDetection={closestCenter}
                          onDragEnd={(e) => handleDragEndLessons(e, modulo.id)}>
                          <SortableContext items={(modulo.lecciones || []).map(l => `lec-${l.id}`)} strategy={verticalListSortingStrategy}>
                            {(modulo.lecciones || []).map((leccion, li) => (
                              <SortableLesson key={leccion.id} lesson={leccion}>
                                {(lessonDragListeners) => (
                                  <LessonCard>
                                    <LessonHeader onClick={() => toggleLesson(leccion.id)}>
                                      <LessonTitle>
                                        <DragHandle {...lessonDragListeners} onClick={e => e.stopPropagation()}>⠿</DragHandle>
                                        <span>{expandedLessons[leccion.id] ? '▾' : '▸'}</span>
                                        <span>Leccion {li + 1}: {leccion.titulo}</span>
                                        <CountBadge>{(leccion.items || []).length} items</CountBadge>
                                      </LessonTitle>
                                      <ActionGroup>
                                        <MiniBtn onClick={e => { e.stopPropagation(); setShowMediaSelector(leccion.id); }}
                                          title="Agregar contenido">➕</MiniBtn>
                                        <MiniBtn onClick={e => { e.stopPropagation(); setShowNameModal({ type: 'leccion', parentId: modulo.id, editId: leccion.id, defaultValue: leccion.titulo }); }}>✏️</MiniBtn>
                                        <MiniBtn $danger onClick={e => { e.stopPropagation(); handleDelete('leccion', leccion.id, leccion.titulo); }}>🗑</MiniBtn>
                                      </ActionGroup>
                                    </LessonHeader>

                                    {expandedLessons[leccion.id] && (
                                      <div style={{ padding: '0.25rem 0 0.5rem' }}>
                                        <DndContext sensors={sensors} collisionDetection={closestCenter}
                                          onDragEnd={(e) => handleDragEndItems(e, leccion.id)}>
                                          <SortableContext items={(leccion.items || []).map(i => `item-${i.id}`)} strategy={verticalListSortingStrategy}>
                                            {(leccion.items || []).map(item => (
                                              <SortableItem key={item.id} item={item}>
                                                {(itemDragListeners) => (
                                                  <ItemRow>
                                                    <ItemInfo>
                                                      <DragHandle {...itemDragListeners}>⠿</DragHandle>
                                                      <span>{TYPE_ICONS[item.media_tipo] || '📄'}</span>
                                                      <span>{item.media_titulo}</span>
                                                    </ItemInfo>
                                                    <ActionGroup>
                                                      {item.duracion_segundos > 0 && (
                                                        <AdminBadge $bg="#dbeafe" $color="#1d4ed8" style={{ fontSize: '0.7rem' }}>
                                                          {formatDuration(item.duracion_segundos)}
                                                        </AdminBadge>
                                                      )}
                                                      {item.tiene_transcripcion ? (
                                                        <AdminBadge $bg="#d1fae5" $color="#065f46" style={{ fontSize: '0.65rem' }}>T</AdminBadge>
                                                      ) : null}
                                                      <MiniBtn $danger onClick={() => handleRemoveItem(item.id)} title="Quitar">✕</MiniBtn>
                                                    </ActionGroup>
                                                  </ItemRow>
                                                )}
                                              </SortableItem>
                                            ))}
                                          </SortableContext>
                                        </DndContext>

                                        {(leccion.items || []).length === 0 && (
                                          <AdminInfoText style={{ textAlign: 'center', padding: '0.5rem', fontSize: '0.8rem' }}>
                                            Sin contenido. Agrega desde la biblioteca.
                                          </AdminInfoText>
                                        )}
                                        <div style={{ margin: '0.25rem 1rem 0' }}>
                                          <AddButton onClick={() => setShowMediaSelector(leccion.id)}>
                                            + Agregar contenido desde biblioteca
                                          </AddButton>
                                        </div>
                                      </div>
                                    )}
                                  </LessonCard>
                                )}
                              </SortableLesson>
                            ))}
                          </SortableContext>
                        </DndContext>

                        <div style={{ margin: '0.25rem 0.75rem 0 1.5rem' }}>
                          <AddButton onClick={() => handleAddLeccion(modulo.id)}>+ Agregar leccion</AddButton>
                        </div>
                      </div>
                    )}
                  </ModuleCard>
                )}
              </SortableModule>
            ))}
          </SortableContext>
        </DndContext>
      )}

      {/* Name Modal (create/edit module or lesson) */}
      {showNameModal && (
        <NameModal
          type={showNameModal.type}
          defaultValue={showNameModal.defaultValue || ''}
          isEdit={!!showNameModal.editId}
          onSubmit={handleNameSubmit}
          onClose={() => setShowNameModal(null)}
          saving={saving}
        />
      )}

      {/* Media Selector */}
      {showMediaSelector && (
        <MediaSelector
          onSelect={handleAddMedia}
          onClose={() => setShowMediaSelector(null)}
          multiple
          title="Agregar contenido a la leccion"
        />
      )}
    </BuilderContainer>
  );
};

// ============================================================
// NAME MODAL (Create/Edit module or lesson)
// ============================================================
const NameModal = ({ type, defaultValue, isEdit, onSubmit, onClose, saving }) => {
  const [value, setValue] = useState(defaultValue);
  const label = type === 'modulo' ? 'Modulo' : 'Leccion';

  return (
    <AdminModalOverlay onClick={onClose}>
      <AdminModalContent onClick={e => e.stopPropagation()}>
        <AdminModalTitle>{isEdit ? 'Editar' : 'Nuevo'} {label}</AdminModalTitle>
        <AdminFormGroup>
          <AdminLabel>Titulo del {label.toLowerCase()}</AdminLabel>
          <AdminInput value={value} onChange={e => setValue(e.target.value)}
            placeholder={`Ej: ${type === 'modulo' ? 'Fundamentos de Farmacologia' : 'Introduccion al tema'}`}
            autoFocus onKeyDown={e => e.key === 'Enter' && value.trim() && onSubmit(value.trim())} />
        </AdminFormGroup>
        <AdminModalActions>
          <AdminButton $ghost onClick={onClose}>Cancelar</AdminButton>
          <AdminButton onClick={() => value.trim() && onSubmit(value.trim())} disabled={saving || !value.trim()}>
            {saving ? 'Guardando...' : isEdit ? 'Guardar' : 'Crear'}
          </AdminButton>
        </AdminModalActions>
      </AdminModalContent>
    </AdminModalOverlay>
  );
};

export default AdminCourseBuilder;
